import { Dialog } from 'primereact/dialog';
import { Formik, Form } from 'formik';
import { forgotPasswordSchema } from 'src/constants/validation.constants';
import { SubTitle } from 'src/components/typography/subtitle';
import { Input } from 'src/components/input/input';
import { B2BButton } from 'src/components/b2bbutton/b2bbutton';
import { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { initEmailSignInWithUuid } from 'src/redux/login/login.async-thunks';
import { showNotification } from 'src/redux/notification/notification.slice';
import { EmailSignInUpButton } from 'src/pages/sign-in/components/email-sign-in-up-button';

type EmailSignInButtonType = 'sign-in' | 'sign-up';

interface EmailSignInDialogueProps extends PropsFromRedux {
  type: EmailSignInButtonType;
  email?: string;
}

const EmailSignInButton: React.FC<EmailSignInDialogueProps> = ({
  type,
  email,
  emailSignInWithUuid,
  showNotification,
}) => {
  const [emailSent, setEmailSent] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [currentEmail, setEmail] = useState(email);
  const [openEmail, setOpenEmail] = useState(false);

  const header = type === 'sign-in' ? 'Email Login Request' : 'Email Sign Up Request';
  const label = type === 'sign-in' ? 'Request Login Link' : 'Request Sign Up Link';


  useEffect(() => {
    setEmail(email);
    const emailIsValid = email?.includes('@') && email?.includes('.');
    setIsValidEmail(emailIsValid || false);
  }, [email]);

  useEffect(() => {
    setIsValidEmail(currentEmail?.includes('@') && currentEmail?.includes('.') || false);
  }, [currentEmail]);

  const handleEmailLoginRequest = async() => {
    if (currentEmail) {
      try {
        await emailSignInWithUuid(currentEmail);
        setEmailSent(true);
        showNotification({
          summary: 'Email sent to your inbox',
          theme: 'success',
        });
      } catch (error) {
        showNotification({
          summary: 'Failed to send email',
          theme: 'warning',
        });
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setIsValidEmail(e.target.value?.includes('@') && e.target.value?.includes('.') || false);
  };

  return (

    <>
      <EmailSignInUpButton
        label='Continue with Email'
        onClick={() => { setOpenEmail(true); }}
      />

      <Dialog
        header={emailSent ? 'Email Sent' : header}
        visible={openEmail}
        className='w-96 p-4 py-0'
        headerClassName='text-black text-left'
        onHide={() => { setOpenEmail(false); setEmailSent(false); setEmail(''); }}
        draggable={false}
        resizable={false}
      >
        { emailSent ? (
          <div className='text-left'>
            <SubTitle
              className='leading-none !text-base'
              color='text-black'
              text={'Please check your email for a login link'}
            />
          </div>
        ) : (
          <Formik
            initialValues={{ email: currentEmail || '' }}
            onSubmit={handleEmailLoginRequest}
            validateOnChange={true}
            validateOnBlur={true}
            validationSchema={forgotPasswordSchema}
            enableReinitialize={true}
          >
            {({ isValid, dirty, isSubmitting, values, errors }) => {
              const canSubmit = isValidEmail && isValid && !errors.email && values.email && (dirty || !!currentEmail);

              return (
                <Form className='mt-2'>
                  <div className='text-left'>
                    <SubTitle
                      className='leading-none !text-base'
                      color='text-black'
                      text={`Please enter your email`}
                    />
                    <Input
                      id={'email'}
                      name={'email'}
                      placeholder={'Email'}
                      className='mt-4'
                      onChange={handleChange}
                    />
                  </div>
                  <div className='mt-4'>
                    <B2BButton
                      disable={!canSubmit || isSubmitting}
                      expand={true}
                      theme='dark'
                      label={label}
                      type='submit'
                      size='small'
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        )}
      </Dialog>
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  emailSignInWithUuid: (email: string) => dispatch(initEmailSignInWithUuid(email)),
  showNotification: (notification: any) => dispatch(showNotification(notification)),
});

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(EmailSignInButton);